/**
 * N2ScorecardPage — Overview. One coherent story:
 *
 *   1. This Week — top 3 conviction reads + compact "other reads" (with
 *      inline model-track-record proof under each notch + CTA to Notches
 *      Desk for covered markets, or a "not covered" note otherwise)
 *   2. How the research works — 3 slim links (Playbook / per-model / Full log)
 *
 * No standalone "Models" section — the model track record is now inline
 * under each notch inside the This Week cards, at the point of decision.
 * Deep methodology + full audit still one click away via sub-nav.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { N2Frame } from './N2Frame';
import { palette } from './theme';
import { Eyebrow, PageTitle, PageLede } from './N2Primitives';
import {
  TodaysReadPanel,
  LoadingPanel, ErrorPanel, ScorecardSubNav,
} from './notchesScorecardShared';
import { useNotchesScorecard } from './useNotchesScorecard';

export const N2ScorecardPage: React.FC = () => {
  const { data, err } = useNotchesScorecard();

  return (
    <N2Frame page="scorecard">
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '48px 32px 40px' }}>
        <ScorecardSubNav />

        <div style={{ paddingTop: 24 }}>
          <Eyebrow>Scorecard</Eyebrow>
          <PageTitle italic="not tips.">
            Research,
          </PageTitle>
          <PageLede>
            Four independent models watch the market: hedge-fund positioning
            (COT), dealer gamma (GEX), IV rank crossings (IV), and anchored-VWAP
            support tests (AVWAP). Every reading is published here as it fires,
            scored against its own horizon, and shown alongside the model's
            public track record on that symbol.
          </PageLede>
        </div>

        {err && <ErrorPanel message={err} />}
        {!data && !err && <LoadingPanel />}
        {data && <>
          <TodaysReadPanel data={data} />
          <HowItWorksFooter />
        </>}
      </div>
    </N2Frame>
  );
};

const HOW_LINKS: { to: string; label: string; blurb: string }[] = [
  {
    to: '/n2/scorecard/playbook',
    label: 'How we build these reads',
    blurb: 'The playbook: matching each model firing to trade structure, sizing, and confluence.',
  },
  {
    to: '/n2/scorecard/cot',
    label: 'Per-model deep dives',
    blurb: 'Every model has its own page — methodology, per-symbol track record, and its own reading log.',
  },
  {
    to: '/n2/scorecard/log',
    label: 'Every published reading',
    blurb: 'The full audit log across all four models, sortable and filterable.',
  },
];

const HowItWorksFooter: React.FC = () => (
  <div style={{ marginTop: 40 }}>
    <div style={{
      fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
      letterSpacing: '0.22em', textTransform: 'uppercase',
      color: palette.ink3, marginBottom: 14,
    }}>How the research works</div>
    <div style={{
      display: 'grid', gap: 12,
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    }}>
      {HOW_LINKS.map((l) => (
        <Link key={l.to} to={l.to} style={{
          textDecoration: 'none',
          padding: '14px 16px',
          border: `1px solid ${palette.line}`, background: palette.paper,
          borderRadius: 3,
          display: 'flex', flexDirection: 'column', gap: 4,
        }}>
          <span style={{
            fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 700,
            letterSpacing: '0.10em', textTransform: 'uppercase',
            color: palette.goldDeep,
          }}>{l.label} →</span>
          <span style={{
            fontFamily: 'var(--sans)', fontSize: 12, color: palette.ink3, lineHeight: 1.5,
          }}>{l.blurb}</span>
        </Link>
      ))}
    </div>
  </div>
);

export default N2ScorecardPage;
